<template>
  <li
    :id="authorId"
    :class="['flex flex-col sm:flex-row sm:items-start bg-faint-grey rounded-md p-4 data-hj-suppress', { 'drag-handle cursor-move': showEdit && showDrag }]"
    data-testid="test-author-details-table">
    <div class="flex flex-1 items-top">
      <DragHandleIcon
        v-if="showEdit && showDrag"
        :class="['mr-2 text-royal-blue-160 shrink-0']"
        :is-decorative="true"/>
      <span
        class="w-6 h-6 flex-shrink-0 flex items-center justify-center border-royal-blue-160 border-2 bg-white rounded-full text-base font-bold text-center mr-4"
        aria-hidden="true">
        {{ props.index +1 }}
      </span>

      <div class="flex flex-col flex-1">
        <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center sm:gap-4">
          <div class="flex-1 min-w-0">
            <div class="break-all">
              <span :id="authorFullNameId">
                <strong
                  class="data-hj-suppress mb-1 block"
                  data-testid="author-fullname">
                  {{ fullName }}
                </strong>
              </span>
              <span
                v-if="email"
                :id="emailId"
                class="data-hj-suppress block mb-1"
                data-testid="author-email"
              >{{ email }}
              </span>

              <p
                v-if="affiliation"
                data-testid="author-affiliation"
                :id="affiliationStringId"
                class="data-hj-suppress ">
                {{ affiliationString }}
              </p>
            </div>
            <FormRadio
              v-if="showCorrespondingRadio"
              :input-id="correspondingAuthorId"
              :modelValue="isCorresponding"
              name="rdoPContact"
              :value="true"
              labelText="Corresponding author"
              @update:modelValue="handleCorrespondingChange"
              class="mt-2"
            />
          </div>

          <div
            class="shrink-0"
            :class="{ 'mb-4 md:mt-0 mt-1': !isValid }">
            <ButtonComponent
              v-if="showEdit"
              label="Edit"
              variant="secondary"
              :id="editAuthorId"
              @customClick="edit"
              class="sm:mt-0 mt-4"
            />
            <div
              v-if="props.isCorresponding && props.showCorrespondingPill"
              :id="correspondingAuthorId"
              :class="[
                'rounded-2xl py-1 px-3 whitespace-nowrap bg-white font-bold inline-block border-2 border-solid border-royal-blue-160',
                { 'sm:mb-4 sm:mt-0 mt-4': isValid, 'mb-4 sm:mt-0 mt-2': !isValid }
              ]"
            >
              Corresponding author
            </div>
          </div>
        </div>

        <div
          v-if="!isValid"
          role="alert">
          <div class="flex items-center gap-2 bg-ruby-red-5 border border-ruby-red-5 p-4 sm:mt-4">
            <WarningTriangleIcon
              title="Error:"
              class="shrink-0 text-ruby-red-100"
              width="1.5em"
              height="1.5em"
            />
            <span>Missing author details</span>
          </div>
        </div>
      </div>
    </div>

    <EditAuthor
      v-if="editing"
      :author="asAuthor"
      @saved="updateAuthor"
      @deleteAuthor="deleteAuthor"
      @cancel="cancelEditing"
    />
  </li>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { Author, Institute } from '@/types'
import EditAuthor from './EditAuthor.vue'
import { validateWithOtherAuthors } from '../../services/validation/authorInfo'
import { useStore } from 'vuex'
import WarningTriangleIcon from '../ui-components/icons/WarningTriangleIcon.vue'
import ButtonComponent from '../ui-components/ButtonComponent.vue'
import FormRadio from '../ui-components/FormRadio.vue'
import DragHandleIcon from '../ui-components/icons/DragHandleIcon.vue'
import { useAuthorManagement } from '@/composables/useAuthorManagement'

const store = useStore()

const props = defineProps({
  id: { required: true, type: String },
  firstName: { required: true, type: String },
  initials: { required: true, type: String },
  lastName: { required: true, type: String },
  isCorresponding: { required: true, type: Boolean },
  email: { required: true, type: String },
  orcid: { required: true, type: String },
  affiliation: { type: Object as () => Institute, default: null },
  index: { required: true, type: Number as () => number },
  showEdit: { required: false, type: Boolean, default: true },
  showCorrespondingPill: { required: false, type: Boolean },
  showCorrespondingRadio: { required: false, type: Boolean },
  showDrag: { type: Boolean, default: false },
})

const { updateAuthor: updateAuthorComposable, deleteAuthor: deleteAuthorComposable } = useAuthorManagement()

var editing = ref(false)

const editAuthorId = ref(`editAuthor-${props.index}`)
const correspondingAuthorId = ref(`correspondingAuthor-${props.index}`)
const authorFullNameId = ref(`authorFullName-${props.index}`)
const emailId = ref(`email-${props.index}`)
const affiliationStringId = ref(`affiliationString-${props.index}`)
const authorId = ref(`authorId-${props.index}`)
const isValid = computed((): boolean => {
  return validateWithOtherAuthors(asAuthor.value, props.index, store.state.activeSubmission.authors as Author[])
})

const fullName = computed((): string => {
  return [props.firstName, props.initials, props.lastName].filter(v => !!v).join(' ')
})

const asAuthor = computed((): Author => {
  return {
    id: props.id,
    firstName: props.firstName,
    initials: props.initials,
    lastName: props.lastName,
    isCorresponding: props.isCorresponding,
    email: props.email,
    orcid: props.orcid,
    affiliation: props.affiliation,
  }
})

const affiliationString = computed((): string => {
  if (props.affiliation) {
    var components = [props.affiliation.name, props.affiliation.city]

    if (props.affiliation.country) {
      components.push(props.affiliation.country.name)
    }

    return components.filter(c => !!c).join(', ')
  }
  return ''
})

function handleCorrespondingChange(value: boolean) {
  updateAuthor({
    id: props.id,
    firstName: props.firstName,
    initials: props.initials,
    lastName: props.lastName,
    isCorresponding: value,
    email: props.email,
    orcid: props.orcid,
    affiliation: props.affiliation,
  })
}

function edit() {
  editing.value = true
}

function updateAuthor(author: Author) {
  updateAuthorComposable(author).then(() => {
    cancelEditing()
  })
}

function deleteAuthor() {
  deleteAuthorComposable(props.id).then(() => {
    cancelEditing()
  })
}

function cancelEditing() {
  editing.value = false
}
</script>
